import type { OffsetDateTime } from "@freightsimple/time/branded-types";
import { Timestamp } from "../DateTime/Display/Timestamp";
import type { RelativeMode } from "../DateTime/Internal/relativeTime";
import { T } from "../Typography/T";

type TimestampCellColor =
  | "default"
  | "muted"
  | "secondary"
  | "green"
  | "red"
  | "orange";

interface TimestampCellProps {
  /** ISO timestamp — renders em dash if undefined */
  value: OffsetDateTime | undefined;
  /** Format preset or custom dayjs format string (default: "short") */
  format?: string | undefined;
  /** Show relative badge below the timestamp */
  relative?: RelativeMode | undefined;
  /** Show only the relative badge, no formatted timestamp */
  relativeOnly?: RelativeMode | undefined;
  /** Size of the relative badge. Defaults to "small". */
  relativeBadgeSize?: "small" | "wide" | undefined;
  /** Drop the "ago"/"in" suffix from the badge label. */
  relativeNoSuffix?: boolean | undefined;
  /** Text color override */
  color?: TimestampCellColor;
}

export function TimestampCell({
  value,
  format = "short",
  relative,
  relativeOnly,
  relativeBadgeSize,
  relativeNoSuffix,
  color,
}: TimestampCellProps) {
  if (!value) {
    return (
      <T color="faint" size="xs">
        {"\u2014"}
      </T>
    );
  }

  if (relativeOnly) {
    return (
      <Timestamp
        value={value}
        relativeOnly={relativeOnly}
        relativeBadgeSize={relativeBadgeSize}
        relativeNoSuffix={relativeNoSuffix}
      />
    );
  }

  const colorProp = color ? { color } : {};

  return (
    <div className="flex flex-col gap-0.5">
      <T size="xs" weight="medium" {...colorProp}>
        <Timestamp value={value} format={format} />
      </T>
      {relative && (
        <div>
          <Timestamp value={value} relativeOnly={relative} />
        </div>
      )}
    </div>
  );
}
